/**
 * Material Requisitions (Feb 2026 · Phase 3)
 * Warehouse-facing view of every MRQ auto-emitted by Child WO
 * generation. Filter by department + status. Each row surfaces the
 * parent WO + SO/ESO chip for full traceability.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { ClipboardTextIcon, ArrowsClockwiseIcon, EyeIcon, TrashIcon, XIcon } from "@phosphor-icons/react";
import { fulfillmentType, FULFILLMENT_BADGE } from "@/lib/fulfillment";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function MaterialRequisitions() {
  const [rows, setRows] = useState([]);
  const [dept, setDept] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(null); // MRQ row opened in the detail drawer

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/material-requisitions");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · Delete MRQ — also drops any stock_movements it posted
    // and the RM-STORE Kardex rows it stamped into inventory_txns.
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.mrq_no}? This reverses every stock issue tied to this requisition.`)) return;
    try {
      await api.delete(`/material-requisitions/${r.id}`);
      toast.success(`${r.mrq_no} · deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const depts = useMemo(() => Array.from(new Set(rows.map(r => r.department))).sort(), [rows]);
  const filtered = useMemo(() => rows.filter(r =>
    (dept === "all" || r.department === dept) && (status === "all" || r.status === status)
  ), [rows, dept, status]);

  return (
    <div className="p-6 space-y-5" data-testid="mrq-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><ClipboardTextIcon size={11} weight="bold" />Production &amp; Planning · Requisitions</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Material Requisitions</h1>
          <div className="text-xs text-zinc-500 mt-1">Full oversight for planners · every exploded requisition (Covering · Spandex Warping · Warping · Weaving) for every Child WO surfaces here</div>
        </div>
        <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <span className="text-zinc-500 font-mono uppercase tracking-widest">Dept:</span>
        {["all", ...depts].map(d => (
          <button key={d} onClick={() => setDept(d)}
            className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${dept === d ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white" : "border-[var(--border)] text-zinc-500 hover:text-white"}`}>{d === "all" ? `All (${rows.length})` : d}</button>
        ))}
        <span className="text-zinc-500 font-mono uppercase tracking-widest ml-4">Status:</span>
        {["all", "Open", "Issued", "Cancelled"].map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${status === s ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white" : "border-[var(--border)] text-zinc-500 hover:text-white"}`}>{s}</button>
        ))}
      </div>
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="mrq-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">MRQ No</th>
              <th className="text-left px-4 py-3">Department</th>
              <th className="text-left px-4 py-3">Fulfillment</th>
              <th className="text-left px-4 py-3">Child WO</th>
              <th className="text-left px-4 py-3">Parent WO</th>
              <th className="text-left px-4 py-3">SO/ESO</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Source Godown</th>
              <th className="text-left px-4 py-3">Materials</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40 align-top" data-testid={`mrq-row-${r.id}`}>
                <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.mrq_no} className="text-white" /></td>
                <td className="px-4 py-3 text-zinc-300">{r.department}</td>
                <td className="px-4 py-3">
                  {(() => {
                    const ft = fulfillmentType(r.department);
                    return ft ? (
                      <span data-testid={`mrq-fulfillment-${r.id}`}
                            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${FULFILLMENT_BADGE[ft] || ""}`}>
                        {ft}
                      </span>
                    ) : <span className="text-zinc-600 text-[10px]">—</span>;
                  })()}
                </td>
                <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.cwo_no} className="text-[var(--accent)]" /></td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.parent_wo_no}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">
                  <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border mr-2 ${r.source_type === "ESO" ? "border-purple-500/30 text-purple-300 bg-purple-500/10" : "border-sky-500/30 text-sky-300 bg-sky-500/10"}`}>{r.source_type || "SO"}</span>
                  {r.source_order_no || "—"}
                </td>
                <td className="px-4 py-3 text-zinc-300">{r.customer_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-amber-300">{r.source_godown_code}</td>
                <td className="px-4 py-3">
                  <div className="space-y-0.5">
                    {(r.lines || []).map((ln, i) => (
                      <div key={i} className="flex items-center gap-3 text-[11px]">
                        <span className="font-mono text-[var(--accent)] truncate max-w-[280px]" title={ln.rm_code}>{ln.rm_code}</span>
                        <span className="font-mono tabular-nums text-zinc-300">{fmt(ln.qty)} {ln.uom}</span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${r.status === "Open" ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`mrq-view-${r.id}`} onClick={() => setView(r)}
                      title="View requisition"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-sky-500/40 hover:bg-sky-500/10 text-sky-300 text-[10px] font-mono uppercase tracking-widest">
                      <EyeIcon size={11} weight="bold" />View
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={11} className="text-center py-16 text-zinc-500">No requisitions match the current filters</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Feb 2026 · MRQ View drawer — read-only full trace (lines, dept,
          godown, parent WO / SO chain) so ops can inspect a requisition
          without leaving the queue. */}
      {view && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="mrq-view-dialog"
             onClick={(e) => { if (e.target === e.currentTarget) setView(null); }}>
          <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-1)]">
              <div>
                <div className="overline text-[var(--accent)]">Material Requisition</div>
                <div className="font-display text-xl text-white font-black tracking-tight">{view.mrq_no}</div>
              </div>
              <button data-testid="mrq-view-close" onClick={() => setView(null)}
                className="p-2 rounded-sm text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]">
                <XIcon size={16} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-4 text-xs">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                <div><div className="overline mb-0.5">Status</div>
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${view.status === "Open" ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>{view.status}</span>
                </div>
                <div><div className="overline mb-0.5">Department</div><div className="text-zinc-200">{view.department || "—"}</div></div>
                <div><div className="overline mb-0.5">Fulfillment</div>
                  {(() => {
                    const ft = fulfillmentType(view.department);
                    return ft ? (
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${FULFILLMENT_BADGE[ft] || ""}`}>{ft}</span>
                    ) : <span className="text-zinc-500">—</span>;
                  })()}
                </div>
                <div><div className="overline mb-0.5">Source Godown</div><div className="font-mono text-amber-300">{view.source_godown_code || "—"}</div></div>
                <div><div className="overline mb-0.5">Child WO</div><div className="font-mono text-[var(--accent)]">{view.cwo_no || "—"}</div></div>
                <div><div className="overline mb-0.5">Parent WO</div><div className="font-mono text-zinc-400">{view.parent_wo_no || "—"}</div></div>
                <div><div className="overline mb-0.5">{view.source_type || "SO"}</div><div className="font-mono text-zinc-400">{view.source_order_no || "—"}</div></div>
                <div className="col-span-2 md:col-span-3"><div className="overline mb-0.5">Customer</div><div className="text-zinc-200">{view.customer_name || "—"}</div></div>
                {view.created_at && (
                  <div><div className="overline mb-0.5">Created</div><div className="font-mono text-zinc-400">{String(view.created_at).slice(0, 19).replace("T", " ")}</div></div>
                )}
                {view.issued_at && (
                  <div><div className="overline mb-0.5">Issued</div><div className="font-mono text-zinc-400">{String(view.issued_at).slice(0, 19).replace("T", " ")}</div></div>
                )}
              </div>

              <div>
                <div className="overline mb-2">Line Items · {(view.lines || []).length}</div>
                <div className="border border-[var(--border)] rounded-sm overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[var(--bg-2)]/60 text-[9px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left w-8">#</th>
                        <th className="px-3 py-1.5 text-left">RM Code</th>
                        <th className="px-3 py-1.5 text-left">Description</th>
                        <th className="px-3 py-1.5 text-right w-24">Qty</th>
                        <th className="px-3 py-1.5 text-left w-16">UOM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(view.lines || []).map((ln, i) => (
                        <tr key={i} data-testid={`mrq-view-line-${i}`} className="border-t border-[var(--border)]">
                          <td className="px-3 py-1.5 font-mono text-zinc-500">{i + 1}</td>
                          <td className="px-3 py-1.5 font-mono text-[var(--accent)]">{ln.rm_code || "—"}</td>
                          <td className="px-3 py-1.5 text-zinc-300">{ln.rm_name || ln.description || "—"}</td>
                          <td className="px-3 py-1.5 font-mono tabular-nums text-right text-zinc-200">{fmt(ln.qty)}</td>
                          <td className="px-3 py-1.5 font-mono text-zinc-400">{ln.uom || "—"}</td>
                        </tr>
                      ))}
                      {(view.lines || []).length === 0 && (
                        <tr><td colSpan={5} className="text-center py-6 text-zinc-500">No line items recorded</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
